import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {
    CheckIcon,
    CubeIcon,
    InformationCircleIcon,
} from "@heroicons/react/outline";
import Loader from "../../components/common/loader/loader";
import ConnectionDocumentHeader from "../../module/document/components/connection-document-header";
import ConnectionDocumentBody from "../../module/document/components/connection-document-body";
import {useConnectionDocumentDetail} from "../../module/document/hooks/useConnectionDocumentDetail";
import {resolveDocumentSlugFromType, resolveDocumentTypeFromSlug} from "../../module/document/constants";
import * as Endpoint from "../endpoints";
import ConfirmPopup from "../../components/common/popup/confirm-popup";
import {getPathParam} from "../../utils/converter";
import {COMPANY, CONFIRMED} from "../../api/endpoints";
import {FALLBACK_VALUE} from "../../module/document/components/document-header-utils";
import {CheckCheckIcon} from "lucide-react";
import {Popover, Transition} from "@headlessui/react";
import {getCustomFormat} from "../../utils/timeUtils";
import ReportViewer from "../../components/partials/report/report-viewer";
import useBackNavigation from "../../hooks/useBackNavigation";

const buildListPath = (slug) => `${Endpoint.SALES + Endpoint.DOCUMENTS_PAGE}/${slug}`;

const INITIAL_SUMMARY = {
    totalRows: null,
    displayRowCount: 0,
    filteredRowCount: 0,
    totalQuantity: 0,
    totalNet: 0,
    isLoading: true,
    hasError: false,
};

const parseNumericValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};


const resolveSupplier = (document) => (
    document?.customer
    ?? document?.company_document?.connection
    ?? document?.customer_company
    ?? document?.company
    ?? null
);

const resolveSupplierName = (document) => {
    if (!document) {
        return null;
    }

    const supplier = resolveSupplier(document);
    const candidates = [
        supplier?.business_name,
        document?.customer_name,
        supplier?.company_name,
        supplier?.name,
        document?.company_name,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string") {
            const trimmed = candidate.trim();
            if (trimmed) {
                return trimmed;
            }
        }
    }

    return null;
};





const ACTION_BUTTON_CLASSNAME = "inline-flex h-8 items-center gap-2 rounded-md border bg-white px-3 text-sm font-medium text-blue-1000 transition hover:bg-blue-50";
const CONFIRM_BUTTON_CLASSNAME = "inline-flex h-8 items-center gap-2 rounded-md border bg-white px-3 text-sm font-medium text-blue-1000 transition hover:bg-blue-50";
const CONNECTION_HEADER_SEGMENT = "connection_header";

const SignatureIcon = ({className = "", ...props}) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...props}
    >
        <path d="M4 14.5c1.4-2 2.8-2 4.2 0s2.8 2 4.2 0 2.8-2 4.2 0 2.8 2 4.2 0" />
        <path d="M3 19h18" />
    </svg>
);

const SIGNATURE_VARIANT_STYLES = {
    success: {
        card: "border-emerald-200 bg-emerald-50 text-emerald-900",
        icon: "bg-emerald-100 text-emerald-600",
    },
    info: {
        card: "border-blue-200 bg-blue-50 text-blue-900",
        icon: "bg-blue-100 text-blue-600",
    },
    warning: {
        card: "border-amber-200 bg-amber-50 text-amber-900",
        icon: "bg-amber-100 text-amber-600",
    },
    neutral: {
        card: "border-gray-200 bg-gray-50 text-gray-600",
        icon: "bg-gray-100 text-gray-500",
    },
};

const SignatureStatusCard = ({variant = "info", icon: IconComponent = SignatureIcon, title, children}) => {
    const styles = SIGNATURE_VARIANT_STYLES[variant] ?? SIGNATURE_VARIANT_STYLES.info;
    return (
        <div className={`flex items-start gap-3 rounded-lg border px-3 py-3 shadow-sm ${styles.card}`}>
            <div className={`flex h-9 w-9 items-center justify-center rounded-full ${styles.icon}`}>
                <IconComponent className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="space-y-1 text-sm leading-5">
                {title ? (
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
                        {title}
                    </p>
                ) : null}
                <div>{children}</div>
            </div>
        </div>
    );
};

const ConnectionDocumentDetailPage = () => {
    const {t} = useTranslation();
    const {document_type: routeSlug, document_id: documentId} = useParams();

    const type = useMemo(() => resolveDocumentTypeFromSlug(routeSlug), [routeSlug]);
    const slug = useMemo(() => routeSlug ?? resolveDocumentSlugFromType(type), [routeSlug, type]);

    const {
        document: fetchedDocument,
        companyId,
        isLoading,
        error,
    } = useConnectionDocumentDetail({type, documentId});
    const {isExporter} = useSelector((state) => state.user ?? {});

    const [document, setDocument] = useState(null);
    const [bodySummary, setBodySummary] = useState(() => ({...INITIAL_SUMMARY}));
    const [areTotalsVisible, setTotalsVisible] = useState(false);
    const [areDocumentTotalsVisible, setDocumentTotalsVisible] = useState(false);
    const [selectedTotalsSection, setSelectedTotalsSection] = useState("general");
    const [confirmDetail, setConfirmDetail] = useState(null);
    const [isOpenConfirmCheck, setOpenConfirmCheck] = useState(false);
    const [isReportViewerOpen, setReportViewerOpen] = useState(false);
    const [reportViewerDetail, setReportViewerDetail] = useState(null);
    const [reportViewerTitle, setReportViewerTitle] = useState("");

    useEffect(() => {
        setDocument(fetchedDocument ?? null);
    }, [fetchedDocument]);

    const handleBack = useBackNavigation(slug ? buildListPath(slug) : null);

    useEffect(() => {
        if (isLoading) {
            setBodySummary((previous) => ({
                ...previous,
                isLoading: true,
            }));
            return;
        }
        setBodySummary((previous) => ({
            ...previous,
            isLoading: false,
        }));
    }, [isLoading]);

    useEffect(() => {
        if (!document || error) {
            setBodySummary({...INITIAL_SUMMARY, isLoading});
            setTotalsVisible(false);
            setDocumentTotalsVisible(false);
        }
    }, [document, error, isLoading]);

    const handleBodySummaryChange = useCallback((summary) => {
        if (!summary || typeof summary !== "object") {
            setBodySummary({...INITIAL_SUMMARY, isLoading: false});
            return;
        }
        setBodySummary({
            ...INITIAL_SUMMARY,
            ...summary,
            isLoading: Boolean(summary.isLoading),
            hasError: Boolean(summary.hasError),
        });
        if (summary && typeof summary === "object") {
            setTotalsVisible(false);
            setDocumentTotalsVisible(false);
        }
    }, []);

    const currencySymbol = document?.currency?.currency_symbol ?? "";
    const documentDisplayName = useMemo(() => (
        document?.media_file
        ?? document?.document_reference
        ?? document?.internal_reference
        ?? document?.code
        ?? t("connection.document.detail.document_title", {defaultValue: "Documento"})
    ), [document, t]);

    const handleReportViewerVisibility = useCallback((nextOpen) => {
        setReportViewerOpen(Boolean(nextOpen));
        if (!nextOpen) {
            setReportViewerDetail(null);
        }
    }, []);

    const handlePreviewDocument = useCallback((fileUrl, label) => {
        if (!fileUrl) {
            return;
        }
        const suffix = label ? ` (${label})` : "";
        setReportViewerDetail({reference: fileUrl});
        setReportViewerTitle(`${documentDisplayName}${suffix}`.trim());
        setReportViewerOpen(true);
    }, [documentDisplayName]);

    const pdfLink = document?.pdf?.reference ?? false;
    const xlsLink = document?.excel?.reference ?? false;
    const confirmLabel = t("connection.document.header.confirm", {defaultValue: "Firma documento"});
    const isConfirmed = useMemo(
        () => String(document?.status ?? "").toLowerCase() === "review",
        [document]
    );
    const canConfirm = useMemo(
        () => isConfirmed && !document?.connection_signed,
        [document, isConfirmed]
    );
    const connectionSigned = Boolean(document?.connection_signed);
    const connectionSignedDate = document?.connection_signed_date ?? null;
    const supplierSignedDate = document?.signed_date ?? null;
    const supplierSigned = Boolean(supplierSignedDate);
    const supplierName = useMemo(() => resolveSupplierName(document), [document]);
    const hasConnectionSignatureInfo = Boolean(
        supplierSigned
        || connectionSigned
        || connectionSignedDate
    );
    const connectionSignatureIconColor = useMemo(() => {
        if (supplierSigned && connectionSignedDate) {
            return "text-emerald-300";
        }
        if (supplierSigned && !connectionSignedDate) {
            return "text-amber-300";
        }
        if (!supplierSigned && (connectionSigned || connectionSignedDate)) {
            return "text-blue-200";
        }
        return "text-white";
    }, [connectionSigned, connectionSignedDate, supplierSigned]);
    const connectionSignatureStatusVariant = useMemo(() => {
        if (supplierSigned && connectionSignedDate) {
            return "success";
        }
        if (connectionSigned || connectionSignedDate) {
            return "info";
        }
        if (supplierSigned) {
            return "warning";
        }
        return "warning";
    }, [connectionSigned, connectionSignedDate, supplierSigned]);
    const connectionSignatureBadge = useMemo(() => {
        if (connectionSignatureStatusVariant === "success") {
            return t("connection.document.confirmation.status_signed", {defaultValue: "Firmato"});
        }
        if (connectionSignatureStatusVariant === "info") {
            return t("connection.document.confirmation.status_in_progress", {defaultValue: "In lavorazione"});
        }
        return t("connection.document.confirmation.status_pending", {defaultValue: "In attesa"});
    }, [connectionSignatureStatusVariant, t]);
    const connectionSignatureBadgeClass = useMemo(() => {
        if (connectionSignatureStatusVariant === "success") {
            return "border-emerald-200 bg-emerald-50 text-emerald-700";
        }
        if (connectionSignatureStatusVariant === "info") {
            return "border-blue-200 bg-blue-50 text-blue-900";
        }
        return "border-amber-200 bg-amber-50 text-amber-700";
    }, [connectionSignatureStatusVariant]);
    const connectionSignatureCards = useMemo(() => {
        if (!hasConnectionSignatureInfo) {
            return [];
        }
        const items = [];
        const supplierNameLabel = supplierName ?? t("connection.document.confirmation.supplier_fallback_name", {defaultValue: supplierName});
        if (supplierSignedDate) {
            const supplierVariant = connectionSignedDate ? "success" : "warning";
            const SupplierIcon = connectionSignedDate ? CheckIcon : SignatureIcon;
            const supplierConfirmationDate = isExporter ? connectionSignedDate : supplierSignedDate;
            items.push(
                <SignatureStatusCard
                    key="supplier-signed"
                    variant={supplierVariant}
                    icon={SupplierIcon}
                    title={t("connection.document.confirmation.supplier_title", {defaultValue: "Conferma fornitore"})}
                >
                    {t(
                        isExporter
                            ? "connection.document.confirmation.supplier_signed_self"
                            : "connection.document.confirmation.supplier_signed",
                        {
                            defaultValue: isExporter
                                ? "Hai confermato il documento il {{date}} alle {{time}}."
                                : "{{supplierName}} ha confermato il documento il {{date}} alle {{time}}.",
                            date: getCustomFormat(supplierConfirmationDate, "DD/MM/YYYY"),
                            time: getCustomFormat(supplierConfirmationDate, "HH:mm"),
                            supplierName: supplierNameLabel,
                        }
                    )}
                </SignatureStatusCard>
            );
        }
        if (connectionSigned && connectionSignedDate) {
            const variant = isExporter ? "info" : "success";
            items.push(
                <SignatureStatusCard
                    key="connection-signed"
                    variant={variant}
                    icon={CheckIcon}
                    title={t("connection.document.confirmation.card_signed_title", {defaultValue: "Documento firmato"})}
                >
                    {t(
                        isExporter
                            ? "connection.document.confirmation.customer_signed"
                            : "app.quotations.confirmed_document",
                        {
                            defaultValue: isExporter
                                ? "Il cliente ha firmato il documento il {{date}} alle {{time}}"
                                : "Documento firmato il {{date}} alle {{time}}",
                            date: getCustomFormat(connectionSignedDate, "DD/MM/YYYY"),
                            time: getCustomFormat(connectionSignedDate, "HH:mm"),
                        }
                    )}
                </SignatureStatusCard>
            );
        }
        if (!connectionSigned || !connectionSignedDate) {
            const variant = connectionSigned ? "info" : "warning";
            items.push(
                <SignatureStatusCard
                    key="connection-pending"
                    variant={variant}
                    icon={SignatureIcon}
                    title={t("connection.document.confirmation.card_pending_title", {defaultValue: "Aggiornamento firma"})}
                >
                    {connectionSigned
                        ? t("connection.document.confirmation.missing_date", {
                            defaultValue: "Firma registrata, data non disponibile.",
                        })
                        : t("connection.document.confirmation.pending", {
                            defaultValue: "In attesa della tua firma sul documento.",
                        })}
                </SignatureStatusCard>
            );
        }
        return items;
    }, [
        connectionSigned,
        connectionSignedDate,
        hasConnectionSignatureInfo,
        isExporter,
        supplierName,
        supplierSignedDate,
        t,
    ]);

    const handleConfirmClick = useCallback(() => {
        if (!companyId) {
            return;
        }
        const resolvedDocumentId = document?.id ?? documentId;
        if (!resolvedDocumentId) {
            return;
        }
        const detailData = {
            title: t("connection.document.confirm.title", {defaultValue: "Firma documento"}),
            message: t("connection.document.confirm.description", {defaultValue: "Confermi di voler approvare questo documento?"}),
            endpoint: getPathParam([
                COMPANY,
                String(companyId),
                CONNECTION_HEADER_SEGMENT,
                String(resolvedDocumentId),
                CONFIRMED,
            ]),
        };
        setConfirmDetail(detailData);
        setOpenConfirmCheck(true);
    }, [companyId, documentId, document, t]);

    const totalsSectionDefinitions = useMemo(() => ([
        {id: "general", label: t("app.documents.totals.general", {defaultValue: "Generale"})},
        {id: "confirmed", label: t("app.documents.totals.confirmed", {defaultValue: "Confermato"})},
        {id: "residual", label: t("app.documents.totals.residual", {defaultValue: "Residuo"})},
    ]), [t]);

    const totalsSource = document?.totals ?? {};
    const totalsBase = totalsSource?.totals ?? totalsSource;
    const documentTotalsSource = document?.document_total ?? document?.documentTotal ?? {};
    const documentTotalsBase = documentTotalsSource?.totals ?? documentTotalsSource;

    const availableTotalsSections = useMemo(() => (
        totalsSectionDefinitions.filter(({id}) => {
            const sectionData = totalsBase?.[id];
            if (!sectionData || typeof sectionData !== "object") {
                return false;
            }
            return Object.values(sectionData).some((value) => parseNumericValue(value) !== null);
        })
    ), [totalsBase, totalsSectionDefinitions]);

    const availableDocumentTotalsSections = useMemo(() => (
        totalsSectionDefinitions.filter(({id}) => {
            const sectionData = documentTotalsBase?.[id];
            if (!sectionData || typeof sectionData !== "object") {
                return false;
            }
            return Object.values(sectionData).some((value) => parseNumericValue(value) !== null);
        })
    ), [documentTotalsBase, totalsSectionDefinitions]);

    useEffect(() => {
        const allAvailable = totalsSectionDefinitions.filter(({id}) => (
            availableTotalsSections.some((section) => section.id === id)
            || availableDocumentTotalsSections.some((section) => section.id === id)
        ));
        if (allAvailable.length === 0) {
            return;
        }
        if (!allAvailable.some(({id}) => id === selectedTotalsSection)) {
            setSelectedTotalsSection(allAvailable[0].id);
        }
    }, [availableDocumentTotalsSections, availableTotalsSections, selectedTotalsSection, totalsSectionDefinitions]);

    const hasTotalsTabs = availableTotalsSections.length > 0;
    const hasDocumentTotalsTabs = availableDocumentTotalsSections.length > 0;

    const fallbackTotals = useMemo(() => (
        hasTotalsTabs
            ? (totalsBase?.general ?? totalsBase ?? {})
            : (totalsBase ?? {})
    ), [hasTotalsTabs, totalsBase]);

    const activeTotals = useMemo(() => (
        hasTotalsTabs
            ? (totalsBase?.[selectedTotalsSection] ?? {})
            : (totalsBase ?? {})
    ), [hasTotalsTabs, selectedTotalsSection, totalsBase]);

    const documentTotalsFallback = useMemo(() => (
        hasDocumentTotalsTabs
            ? (documentTotalsBase?.general ?? documentTotalsBase ?? {})
            : (documentTotalsBase ?? {})
    ), [documentTotalsBase, hasDocumentTotalsTabs]);

    const documentTotalsActive = useMemo(() => (
        hasDocumentTotalsTabs
            ? (documentTotalsBase?.[selectedTotalsSection] ?? {})
            : (documentTotalsBase ?? {})
    ), [documentTotalsBase, hasDocumentTotalsTabs, selectedTotalsSection]);

    const formatCurrencyValue = useCallback((value) => {
        const numeric = parseNumericValue(value);
        if (numeric === null) {
            return FALLBACK_VALUE;
        }
        const formatted = numeric.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return currencySymbol ? `${formatted} ${currencySymbol}` : formatted;
    }, [currencySymbol]);

    const formatMeasurementValue = useCallback((value, fractionDigits = 2) => {
        const numeric = parseNumericValue(value);
        if (numeric === null) {
            return FALLBACK_VALUE;
        }
        return numeric.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: fractionDigits,
        });
    }, []);

    const formatCountValue = useCallback((value) => {
        const numeric = parseNumericValue(value);
        if (numeric === null) {
            return FALLBACK_VALUE;
        }
        return numeric.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }, []);

    const totalUnitPriceRaw = parseNumericValue(activeTotals.total_unit_price)
        ?? parseNumericValue(fallbackTotals.total_unit_price);
    const totalDiscountRaw = parseNumericValue(activeTotals.total_discount)
        ?? parseNumericValue(fallbackTotals.total_discount);
    const totalNetPriceRaw = parseNumericValue(activeTotals.total_net_price)
        ?? parseNumericValue(fallbackTotals.total_net_price);

    const summaryTotalFallback = useMemo(
        () => parseNumericValue(bodySummary.totalNet),
        [bodySummary.totalNet]
    );

    const formattedTotal = useMemo(() => (
        formatCurrencyValue(totalNetPriceRaw ?? summaryTotalFallback)
    ), [formatCurrencyValue, totalNetPriceRaw, summaryTotalFallback]);

    const totalsBreakdown = useMemo(() => {
        if (!totalsBase || Object.keys(totalsBase).length === 0) {
            return [];
        }
        return [
            {
                id: "total-unit-price",
                label: t("app.documents.total_unit_price", {defaultValue: "Totale unitario"}),
                value: formatCurrencyValue(totalUnitPriceRaw),
            },
            {
                id: "total-discount",
                label: t("app.documents.total_discount", {defaultValue: "Totale sconti"}),
                value: formatCurrencyValue(totalDiscountRaw),
            },
            {
                id: "total-net-price",
                label: t("app.documents.total_net_price", {defaultValue: "Totale netto"}),
                value: formattedTotal,
                isHighlight: true,
            },
        ].filter((item) => item.value !== FALLBACK_VALUE);
    }, [formatCurrencyValue, formattedTotal, t, totalDiscountRaw, totalUnitPriceRaw, totalsBase]);

    const totalRowsMetricRaw = parseNumericValue(documentTotalsActive.total_rows)
        ?? parseNumericValue(documentTotalsFallback.total_rows);
    const totalVolumeMetricRaw = parseNumericValue(documentTotalsActive.total_volume)
        ?? parseNumericValue(documentTotalsFallback.total_volume);
    const totalNetWeightMetricRaw = parseNumericValue(documentTotalsActive.total_net_weight)
        ?? parseNumericValue(documentTotalsFallback.total_net_weight);
    const totalGrossWeightMetricRaw = parseNumericValue(documentTotalsActive.total_gross_weight)
        ?? parseNumericValue(documentTotalsFallback.total_gross_weight);
    const totalBoxMetricRaw = parseNumericValue(documentTotalsActive.total_box)
        ?? parseNumericValue(documentTotalsFallback.total_box);

    const documentTotalsBreakdown = useMemo(() => {
        if (!documentTotalsBase || Object.keys(documentTotalsBase).length === 0) {
            return [];
        }
        return [
            {
                id: "document-total-rows",
                label: t("app.documents.metrics.total_rows", {defaultValue: "Righe totali"}),
                value: formatCountValue(totalRowsMetricRaw),
            },
            {
                id: "document-total-volume",
                label: t("app.documents.metrics.total_volume", {defaultValue: "Volume totale (m³)"}),
                value: formatMeasurementValue(totalVolumeMetricRaw, 3),
            },
            {
                id: "document-total-net-weight",
                label: t("app.documents.metrics.total_net_weight", {defaultValue: "Peso netto totale (kg)"}),
                value: formatMeasurementValue(totalNetWeightMetricRaw, 3),
            },
            {
                id: "document-total-gross-weight",
                label: t("app.documents.metrics.total_gross_weight", {defaultValue: "Peso lordo totale (kg)"}),
                value: formatMeasurementValue(totalGrossWeightMetricRaw, 3),
            },
            {
                id: "document-total-box",
                label: t("app.documents.metrics.total_box", {defaultValue: "Box totali"}),
                value: formatMeasurementValue(totalBoxMetricRaw, 2),
            },
        ].filter((item) => item.value !== FALLBACK_VALUE);
    }, [documentTotalsBase, formatCountValue, formatMeasurementValue, t, totalBoxMetricRaw, totalGrossWeightMetricRaw, totalNetWeightMetricRaw, totalRowsMetricRaw, totalVolumeMetricRaw]);

    const hasTotalsData = totalsBreakdown.length > 0;
    const hasDocumentTotalsData = documentTotalsBreakdown.length > 0;

    useEffect(() => {
        if (!hasTotalsData) {
            setTotalsVisible(false);
        }
    }, [hasTotalsData]);

    useEffect(() => {
        if (!hasDocumentTotalsData) {
            setDocumentTotalsVisible(false);
        }
    }, [hasDocumentTotalsData]);

    let content = null;

    if (isLoading) {
        content = (
            <div className="flex h-64 items-center justify-center">
                <Loader />
            </div>
        );
    } else if (error) {
        content = (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-6 text-center text-sm text-rose-700">
                {t("connection.document.detail.error", {defaultValue: "Si è verificato un errore durante il caricamento del documento."})}
            </div>
        );
    } else if (!document) {
        content = (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-700">
                {t("connection.document.detail.missing", {defaultValue: "Documento non disponibile."})}
            </div>
        );
    } else {
        content = (
            <div className="">
                <ConnectionDocumentHeader document={document} />
                <ConnectionDocumentBody
                    companyId={companyId}
                    documentId={document?.id ?? documentId}
                    currencySymbol={currencySymbol}
                    onSummaryChange={handleBodySummaryChange}
                />
            </div>
        );
    }

    const showSummaryFooter = Boolean(document && !error);

    return (
        <>
            <ReportViewer
                isOpen={isReportViewerOpen}
                setIsOpen={handleReportViewerVisibility}
                title={reportViewerTitle}
                reportDetail={reportViewerDetail ?? false}
                isActiveConfirm={false}
            />
            <div className="flex min-h-screen flex-col bg-white">

            <main className="flex-1 overflow-auto bg-slate-50">
                <div className="mx-auto w-full max-w-full  pb-40 ">
                    {content}
                </div>
            </main>

            {showSummaryFooter && (
                <footer className="md:sticky fixed inset-x-0 bottom-0 z-[50] border-t border-gray-900/50 bg-gray-900">
                    <div className="mx-auto flex flex-col gap-4 px-4 py-3 text-white sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
                        <div className="order-2 flex w-full flex-wrap items-center gap-3 sm:order-1 sm:w-auto sm:flex-nowrap">
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="h-8 rounded-md border border-white/30 px-3 text-sm font-medium text-white hover:bg-white/10"
                                >
                                    {t("app.common.back", {defaultValue: "Indietro"})}
                                </button>
                                {hasConnectionSignatureInfo && (
                                    <Popover className="relative">
                                        <Popover.Button
                                            className="group relative flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white shadow-sm transition hover:border-white/40 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
                                            aria-label={t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                            title={t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                        >
                                            <SignatureIcon className={`h-5 w-5 transition duration-150 group-hover:scale-105 ${connectionSignatureIconColor}`} aria-hidden="true" />
                                            <span className="sr-only">
                                                {t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                            </span>
                                        </Popover.Button>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-150"
                                            enterFrom="opacity-0 translate-y-1"
                                            enterTo="opacity-100 translate-y-0"
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100 translate-y-0"
                                            leaveTo="opacity-0 translate-y-1"
                                        >
                                            <Popover.Panel className="fixed inset-x-4 bottom-[4.5rem] z-30 w-auto max-w-full transform origin-bottom rounded-2xl sm:absolute sm:bottom-full sm:left-0 sm:mb-4 sm:w-[26rem] sm:max-w-[calc(100vw-2rem)] sm:origin-bottom-left">
                                                <div className="rounded-2xl border border-gray-200 bg-white/95 p-4 text-gray-900 shadow-2xl ring-1 ring-black/5 backdrop-blur sm:p-5">
                                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                {t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                                            </p>

                                                        </div>
                                                        <span className={`inline-flex w-auto items-center gap-1.5 text-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${connectionSignatureBadgeClass}`}>
                                                            <span className="inline-block h-2 w-2 rounded-full bg-current" />
                                                            {connectionSignatureBadge}
                                                        </span>
                                                    </div>

                                                    {
                                                        connectionSignatureCards.length > 0 && (
                                                            <div className="mt-4 max-h-60 space-y-3 overflow-y-auto text-left sm:max-h-80">
                                                                {connectionSignatureCards}
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </Popover.Panel>
                                        </Transition>
                                    </Popover>
                                )}
                                {pdfLink && (
                                    <button
                                        type="button"
                                        onClick={() => handlePreviewDocument(pdfLink, "PDF")}
                                        className={ACTION_BUTTON_CLASSNAME}
                                    >
                                        <span>PDF</span>
                                    </button>
                                )}
                                {xlsLink && (
                                    <button
                                        type="button"
                                        onClick={() => handlePreviewDocument(xlsLink, "XLS")}
                                        className={ACTION_BUTTON_CLASSNAME}
                                    >
                                        <span>XLS</span>
                                    </button>
                                )}
                                {canConfirm && (
                                    <button
                                        type="button"
                                        onClick={handleConfirmClick}
                                        className={CONFIRM_BUTTON_CLASSNAME}
                                        aria-label={confirmLabel}
                                        title={confirmLabel}
                                    >
                                        <CheckCheckIcon className="h-4 w-4" aria-hidden="true" />
                                        <span>{confirmLabel}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="order-1 flex w-full flex-col gap-2 text-left sm:order-2 sm:w-auto sm:items-end sm:text-right">
                            <dl
                                className="relative space-y-3 rounded-lg bg-gray-800/70 p-3 text-white sm:space-y-3 sm:rounded-none sm:bg-transparent sm:p-0"
                                aria-busy={bodySummary.isLoading}
                            >
                                <div
                                    className={`flex flex-wrap items-center justify-start gap-3 text-white sm:justify-end ${bodySummary.isLoading ? "animate-pulse" : ""}`}
                                >
                                    <dt className="w-full text-xs font-semibold uppercase tracking-wide text-gray-300 sm:w-auto sm:text-sm sm:normal-case sm:tracking-normal">
                                        {t("app.documents.total", {defaultValue: "Totale"})}
                                    </dt>
                                    <dd className="text-2xl font-semibold text-white">
                                        {formattedTotal}
                                    </dd>
                                    {hasTotalsData && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setDocumentTotalsVisible(false);
                                                setTotalsVisible((prev) => !prev);
                                            }}
                                            className="rounded-full p-1 text-gray-300 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900"
                                            aria-label={t("app.documents.view_total_details", {defaultValue: "Mostra dettaglio totale"})}
                                            aria-expanded={areTotalsVisible}
                                        >
                                            <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    )}
                                    {hasDocumentTotalsData && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setTotalsVisible(false);
                                                setDocumentTotalsVisible((prev) => !prev);
                                            }}
                                            className="rounded-full p-1 text-gray-300 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900"
                                            aria-label={t("app.documents.view_document_totals", {defaultValue: "Mostra dettagli volume"})}
                                            aria-expanded={areDocumentTotalsVisible}
                                        >
                                            <CubeIcon className="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                                {hasDocumentTotalsData && areDocumentTotalsVisible && (
                                    <div
                                        className="absolute bottom-12 right-0 z-20 w-72 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-lg"
                                    >
                                        {hasDocumentTotalsTabs && (
                                            <div className="mb-3 flex items-center gap-1 rounded-md bg-gray-100 p-1 text-xs font-medium text-gray-600">
                                                {availableDocumentTotalsSections.map((section) => {
                                                    const isActiveSection = section.id === selectedTotalsSection;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={`connection-document-totals-${section.id}`}
                                                            onClick={() => setSelectedTotalsSection(section.id)}
                                                            className={`flex-1 rounded-md px-2 py-1 transition ${isActiveSection ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
                                                            aria-pressed={isActiveSection}
                                                        >
                                                            {section.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <dl className="space-y-2 text-sm">
                                            {documentTotalsBreakdown.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between gap-4"
                                                >
                                                    <dt className="font-medium text-gray-600">
                                                        {item.label}
                                                    </dt>
                                                    <dd className={item.isHighlight ? "font-semibold text-gray-900" : "font-medium text-gray-700"}>
                                                        {item.value}
                                                    </dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </div>
                                )}
                                {hasTotalsData && areTotalsVisible && (
                                    <div
                                        className="absolute bottom-12 right-0 z-20 w-64 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-lg"
                                    >
                                        {hasTotalsTabs && (
                                            <div className="mb-3 flex items-center gap-1 rounded-md bg-gray-100 p-1 text-xs font-medium text-gray-600">
                                                {availableTotalsSections.map((section) => {
                                                    const isActiveSection = section.id === selectedTotalsSection;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={`connection-totals-${section.id}`}
                                                            onClick={() => setSelectedTotalsSection(section.id)}
                                                            className={`flex-1 rounded-md px-2 py-1 transition ${isActiveSection ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
                                                            aria-pressed={isActiveSection}
                                                        >
                                                            {section.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <dl className="space-y-2 text-sm">
                                            {totalsBreakdown.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between gap-4"
                                                >
                                                    <dt className={item.isHighlight ? "font-semibold text-gray-900" : "font-medium text-gray-600"}>
                                                        {item.label}
                                                    </dt>
                                                    <dd className={item.isHighlight ? "font-semibold text-gray-900" : "font-medium text-gray-700"}>
                                                        {item.value}
                                                    </dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </div>
                                )}
                            </dl>
                        </div>
                    </div>
                </footer>
            )}
            {isOpenConfirmCheck && confirmDetail && (
                <ConfirmPopup
                    isOpen={isOpenConfirmCheck}
                    setIsOpen={() => {
                        setOpenConfirmCheck(false);
                        setConfirmDetail(null);
                    }}
                    detail={confirmDetail}
                    setDetail={setDocument}
                />
            )}
        </div>
        </>
    );
};

export default ConnectionDocumentDetailPage;
